import * as Highcharts from "highcharts";
import {
    Component,
    Inject,
    Input,
    computed,
    inject,
    signal
} from "@angular/core";
import { VisualizationEntity } from "@arch/domain/entity/visualization.entity";
import { HighchartsRenderComponent } from "@shared/components/highcharts-render/highcharts-render.component";
import { ResultPerformanceType } from "../report-performance/report-performance.component";
import { ThemeService } from "@arch/domain/application/services/theme.service";
import { ThemesCustom } from "../report-performance/report-performance.theme";

@Component({
    selector: "app-chart-performance-report",
    templateUrl: "./chart-performance-report.component.html",
    styleUrl: "./chart-performance-report.component.scss",
    standalone: true,
    imports: [HighchartsRenderComponent]
})
export class ChartPerformanceReportComponent {
    @Input() set results(value: ResultPerformanceType[]) {
        this.resultsSignal.set(value);
    }
    @Input() set currentTab(value: string) {
        this.currentTabSignal.set(value);
    }
    @Input({ required: false }) pdf: boolean;

    private readonly themeService = inject(ThemeService);
    private resultsSignal = signal<ResultPerformanceType[]>([]);
    private currentTabSignal = signal<string>("reference");
    public visualization: VisualizationEntity;
    public chartId: string;

    public chartStructure = computed(() => {
        const data: Array<{
            name: string;
            y: number;
            color: string;
        }> = this.resultsSignal().map((result: ResultPerformanceType) => ({
            name: `${this.getLabelByType(result.type)} (${result.series.length})`,
            y: result.series.length,
            color: this.getColorByType(result.type)
        }));

        const { total, label } = this.getTotalIndicators();

        const structure = {
            credits: {
                enabled: false
            },
            chart: {
                height: "120px",
                type: "pie",
                backgroundColor: "transparent",
                custom: {},
                events: {
                    render: (event: any) => {
                        const chart = event.target,
                            series = chart.series[0];
                        let customLabel = chart.options.chart.custom.label;

                        if (!customLabel) {
                            customLabel = chart.options.chart.custom.label =
                                chart.renderer
                                    .label(
                                        `
                        <span style="color: var(--color-text-primary); font: ${this.pdf ? "var(--font-text-12-semi-bold)" : "var(--font-text-16-semi-bold)"}; padding: 0 var(--spacing-16);">
                            ${total}
                        </span><br/>
                        <span style="font: ${this.pdf ? "var(--font-text-10-regular)" : "var(--font-text-14-regular)"}; color: var(--color-text-primary);">
                            ${label}
                        </span>
                  `
                                    )
                                    .css({
                                        color: "#000",
                                        textAnchor: "middle"
                                    })
                                    .add();
                        }
                        const x = series.center[0] + chart.plotLeft - 3;
                        const y =
                            series.center[1] +
                            chart.plotTop -
                            customLabel.attr("height") / 2;
                        1;

                        customLabel.attr({
                            x,
                            y
                        });
                    }
                }
            },
            accessibility: {
                point: {
                    valueSuffix: "%"
                }
            },
            pane: {
                background: null
            },
            legend: {
                width: "180px",
                layout: "vertical",
                align: "right",
                itemMarginTop: 0,
                itemMarginBottom: 3,
                verticalAlign: "middle",
                useHTML: true,
                labelFormatter: function (this: any) {
                    if (this.name.includes("objetivo")) {
                        return (
                            '<span style="display: inline-flex; align-items: center;">' +
                            '<span class="circle-goals-icon"  style="background-color: var(--color-expand-collapse-default-bg);position: absolute; left: -20px; top: 0px;"></span>' +
                            "Alcançaram objetivo (" +
                            this.y +
                            ")" +
                            "</span>"
                        );
                    }

                    return this.name;
                },
                itemStyle: {
                    color: "var(--color-button-group-text)",
                    font: `var(--font-size-${this.pdf ? "10" : "12"}) Open Sans`,
                    fontWeight: "400",
                    whiteSpace: "nowrap",
                    overflow: "unset",
                    textOverflow: "unset",
                    ...(this.pdf && {
                        cursor: "default",
                        pointerEvents: "none"
                    })
                },
                itemHoverStyle: { color: "var(--color-text-cyan-primary)" },
                className: "chart-performance-report-component_chart",
                navigation: {
                    enabled: false
                }
            },
            title: {
                text: null
            },
            subtitle: {
                text: null
            },
            tooltip: {
                pointFormat: "{series.name}: <b>{point.percentage:.0f}%</b>"
            },
            plotOptions: {
                pie: {
                    size: "112px",
                    allowPointSelect: true,
                    cursor: "pointer",
                    dataLabels: {
                        enabled: false
                    },
                    borderWidth: 0,
                    showInLegend: true
                },
                series: {
                    ...(this.pdf && {
                        enableMouseTracking: false,
                        allowPointSelect: false,
                        cursor: "default",
                        showInLegend: true,
                        legendItemClick: false,
                        states: {
                            hover: {
                                enabled: false
                            },
                            select: {
                                enabled: false
                            },
                            inactive: {
                                enabled: false
                            },
                            click: {
                                enabled: false
                            }
                        }
                    })
                }
            },
            series: [
                {
                    name: "Empresas",
                    type: "pie",
                    innerSize: "80%",
                    data
                }
            ]
        } as Highcharts.Options;

        return structure as Highcharts.Options;
    });

    private getColorByType(type: string): string {
        switch (type) {
            case "good":
                return this.getThemeCustomColor("cyan-primary");
            case "stable":
                return this.getThemeCustomColor("yellow-dark");
            case "bad":
                return this.getThemeCustomColor("purple-dark");
            default:
                return "";
        }
    }

    private getThemeCustomColor(color: string) {
        const themename = this.themeService.getThemeName();
        return ThemesCustom[themename]?.[color] || "";
    }

    private getLabelByType(type: string): string {
        if (["reference", "budget"].includes(this.currentTabSignal())) {
            switch (type) {
                case "good":
                    return "Acima";
                case "stable":
                    return "Dentro";
                case "bad":
                    return "Abaixo";
                default:
                    return "";
            }
        } else if (["goals"].includes(this.currentTabSignal())) {
            switch (type) {
                case "good":
                    return "Alcançaram objetivo";
                case "bad":
                    return "Não alcançaram";
                default:
                    return "";
            }
        } else {
            switch (type) {
                case "good":
                    return "Melhorou";
                case "stable":
                    return "Estabilizou";
                case "bad":
                    return "Piorou";
                default:
                    return "";
            }
        }
    }

    private getTotalIndicators(): { total: number; label: string } {
        const total = this.resultsSignal().reduce(
            (acc, result) => acc + result.series.length,
            0
        );
        return {
            total,
            label: total > 1 ? "Indicadores" : "Indicador"
        };
    }
}
